import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRunnerSchedule,
  deleteRunnerSchedule,
  fetchRunnerSchedule,
  fetchRunnerScheduleRuns,
  fetchRunnerSchedules,
  runRunnerSchedule,
  setRunnerScheduleEnabled,
  updateRunnerSchedule,
  type RunnerScheduleInput,
} from '../../lib/api/runnerSchedule';
import { queryKeys } from './queryKeys';

export const useRunnerSchedules = (token: string | null, projectId: string, page = 1, enabled = true) =>
  useQuery({
    queryKey: queryKeys.runnerSchedules.list(projectId, page),
    queryFn: () => fetchRunnerSchedules(token!, projectId, page),
    enabled: Boolean(token && projectId) && enabled,
    refetchInterval: 60_000,
  });

export const useRunnerSchedule = (token: string | null, projectId: string, id: string) =>
  useQuery({
    queryKey: queryKeys.runnerSchedules.detail(projectId, id),
    queryFn: () => fetchRunnerSchedule(token!, projectId, id),
    enabled: Boolean(token && projectId && id),
  });

export const useRunnerScheduleRuns = (token: string | null, projectId: string, id: string, page = 1, pageSize = 10) =>
  useQuery({
    queryKey: queryKeys.runnerSchedules.runs(projectId, id, page, pageSize),
    queryFn: () => fetchRunnerScheduleRuns(token!, projectId, id, page, pageSize),
    enabled: Boolean(token && projectId && id),
    refetchInterval: 60_000,
  });

const useScheduleInvalidation = (projectId: string) => {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.runnerSchedules.all(projectId) });
};

export const useCreateRunnerScheduleMutation = (token: string | null, projectId: string) => {
  const onSuccess = useScheduleInvalidation(projectId);
  return useMutation({
    mutationFn: (input: RunnerScheduleInput) => createRunnerSchedule(token!, projectId, input),
    onSuccess,
  });
};

export const useUpdateRunnerScheduleMutation = (token: string | null, projectId: string) => {
  const onSuccess = useScheduleInvalidation(projectId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<RunnerScheduleInput> }) =>
      updateRunnerSchedule(token!, projectId, id, input),
    onSuccess,
  });
};

export const useSetRunnerScheduleEnabledMutation = (token: string | null, projectId: string) => {
  const onSuccess = useScheduleInvalidation(projectId);
  return useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      setRunnerScheduleEnabled(token!, projectId, id, isEnabled),
    onSuccess,
  });
};

export const useDeleteRunnerScheduleMutation = (token: string | null, projectId: string) => {
  const onSuccess = useScheduleInvalidation(projectId);
  return useMutation({ mutationFn: (id: string) => deleteRunnerSchedule(token!, projectId, id), onSuccess });
};

export const useRunRunnerScheduleMutation = (token: string | null, projectId: string) => {
  const client = useQueryClient();
  const invalidate = useScheduleInvalidation(projectId);
  return useMutation({
    mutationFn: (id: string) => runRunnerSchedule(token!, projectId, id),
    onSuccess: async () => {
      await Promise.all([invalidate(), client.invalidateQueries({ queryKey: queryKeys.daemonTriggers.all })]);
    },
  });
};
